<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
          <v-spacer />
          <div class="ml-4 text-right">
            <v-tooltip max-width="300" bottom>
              <template #activator="{ on }">
                <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
                </base-chip>
              </template>
              <span>Remember to save your changes before leaving!</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-divider class="mt-2" />
    </v-container>

    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-space-between">
          <h2>{{ originalRole.name }}</h2>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #content>
              <data-table :headers="tableHeaders" :items="resourceList" disable-pagination hide-default-footer>
                <template #item="{ item }">
                  <td>
                    {{ item.name }}
                  </td>
                  <td>
                    <v-checkbox
                      :disabled="!isPermittedTo('update') || isLoading"
                      :false-value="false"
                      :input-value="valueForResource(item.slug, 'create')"
                      :title="item.create"
                      :true-value="true"
                      class="mt-0"
                      hide-details
                      @change="onUpdateResource(item.slug, 'create', $event)"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-checkbox
                      :disabled="!isPermittedTo('update') || isLoading"
                      :false-value="false"
                      :input-value="valueForResource(item.slug, 'read')"
                      :title="item.read"
                      :true-value="true"
                      class="mt-0"
                      hide-details
                      @change="onUpdateResource(item.slug, 'read', $event)"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-checkbox
                      :disabled="!isPermittedTo('update') || isLoading"
                      :false-value="false"
                      :input-value="valueForResource(item.slug, 'update')"
                      :title="item.update"
                      :true-value="true"
                      class="mt-0"
                      hide-details
                      @change="onUpdateResource(item.slug, 'update', $event)"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-checkbox
                      :disabled="!isPermittedTo('update') || isLoading"
                      :false-value="false"
                      :input-value="valueForResource(item.slug, 'delete')"
                      :title="item.delete"
                      :true-value="true"
                      class="mt-0"
                      hide-details
                      @change="onUpdateResource(item.slug, 'delete', $event)"
                    ></v-checkbox>
                  </td>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Resources"
      @reset="resetRole"
      @save="showSaveModal = true"
    />

    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Role"
      persistent
      @modal-confirm="onModalSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <v-row>
        <v-col sm="6">
          <span>Are you sure you want to save these changes?</span>
        </v-col>
      </v-row>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
// Components
import BaseChip from '@/components/chips/base-chip'
import PageLoader from '@/components/page-loader'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import FilterAccordion from '@/components/filter-accordion'
import DataTable from '@/components/data-table'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
import DataModification from '@/mixins/data-modification'
// Services
import { roles, resources } from '@/services/offers'

export default {
  name: 'RolesUpdate',
  components: {
    BaseChip,
    FabActions,
    PageLoader,
    GenericModal,
    FilterAccordion,
    DataTable,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    originalRole: {},
    editedRole: {},
    resourceList: [],
    searchKey: 'name',
    queryPagination: false,
    querySearch: false,
    tableHeaders: [
      { text: 'Resource', value: 'slug' },
      { text: 'Create', value: 'create', sortable: false },
      { text: 'Read', value: 'read', sortable: false },
      { text: 'Update', value: 'update', sortable: false },
      { text: 'Delete', value: 'delete', sortable: false },
    ],
    showSaveModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions']),
    breadcrumbs() {
      return [
        { text: 'Roles', to: { name: 'roles.management' }, exact: true },
        { text: this.originalRole.name || 'loading', disabled: true },
      ]
    },
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
    hasChanges() {
      return this.hasDataChanged(this.originalRole, this.editedRole)
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    await Promise.all([this.getRole(), this.getResources()])
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    /**
     * Retrieve role data from API
     */
    async getRole() {
      this.isLoading = true

      try {
        this.originalRole = await roles.getRole(this.$route.params.slug)
        this.editedRole = this.clone(this.originalRole)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve role',
        })
      }

      this.isLoading = false
    },
    /**
     * Retrieve resources data from API
     */
    async getResources() {
      try {
        this.resourceList = await resources.getResources()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve list of resources',
        })
      }
    },
    async resetRole() {
      this.$notify('Changes have been reset')
      await this.getRole()
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onModalSaveConfirm() {
      await this.updateRole()
      await this.getRole()
      this.showSaveModal = false
    },
    removeResourceFromRole(resourceSlug) {
      const indexToRemove = this.editedRole.resources.findIndex(resource => resource.slug === resourceSlug)
      this.editedRole.resources.splice(indexToRemove, 1)
      this.$notify('Resource removed - click "Save Role" to keep changes')
    },
    /**
     * Send new data for the current role to the API.
     */
    async updateRole() {
      this.isLoading = true

      try {
        await roles.updateRole(this.$route.params.slug, this.editedRole)
        this.$notify({
          type: 'success',
          text: 'Changes saved successfully',
        })
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to save role',
        })
      }

      this.isLoading = false
    },
    valueForResource(resource, action) {
      const foundResource = this.editedRole.resources.find(({ slug }) => slug === resource)
      if (!foundResource) return false
      return foundResource[action]
    },
    onUpdateResource(resource, action, value) {
      const index = this.editedRole.resources.findIndex(({ slug }) => slug === resource)
      if (index !== -1) {
        this.$set(this.editedRole.resources, index, { ...this.editedRole.resources[index], [action]: value })
      } else {
        this.editedRole.resources.push({
          slug: resource,
          [action]: value,
        })
      }
    },
  },
}
</script>
